// Vista previa completa de una propiedad para la martillera que aprueba.
// Replica cómo se verá la publicación en el sitio público (Card + Detail),
// muestra TODA la información obligatoria (marcando faltantes), calcula una
// puntuación de completitud + calidad, permite editar inline casi todos los
// campos y ver la documentación (PDF) embebida. Aprobación/rechazo integrados.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import styles from './PropertyPreviewModal.module.css';
import { editProperty } from '../../Redux/Actions/actions';
import ApprovalActions from '../ApprovalActions/ApprovalActions';
import {
  EditableText,
  EditableNumber,
  EditableTextarea,
  EditableSelect,
  EditableCheckboxGroup,
} from './InlineEditors';
import {
  amenityLabels,
  characteristicLabels,
  environmentLabels,
  serviceLabels,
  detailLabels,
  propertyTypeOptions,
  propertyStateOptions,
  currencyOptions,
  commissionTypeOptions,
  formatPrice,
  isTruthy,
  isLandType,
  isValidYoutubeUrl,
  isPdfUrl,
  isImageUrl,
  getFileNameFromUrl,
  getLabelFromOptions,
  typesWithCantidad,
} from './propertyLabels';
import { computePropertyScore, getScoreLevel } from './propertyScore';

const youtubeEmbedUrl = (url) => {
  if (!url) return null;
  const short = url.match(/youtu\.be\/([\w-]+)/);
  if (short) return `https://www.youtube.com/embed/${short[1]}`;
  const long = url.match(/[?&]v=([\w-]+)/);
  if (long) return `https://www.youtube.com/embed/${long[1]}`;
  return null;
};

const PropertyPreviewModal = ({ propertyId, onClose, onSaved }) => {
  const dispatch = useDispatch();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [activePhoto, setActivePhoto] = useState(0);
  const [docPreview, setDocPreview] = useState(null);
  const [savedOnce, setSavedOnce] = useState(false);

  const fetchProperty = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`/property/${propertyId}`);
      setData(res.data.data);
      setDirty(false);
    } catch (error) {
      console.error('Error al cargar la propiedad:', error);
      toast.error('No se pudo cargar la propiedad.');
    } finally {
      setLoading(false);
    }
  }, [propertyId]);

  useEffect(() => {
    fetchProperty();
  }, [fetchProperty]);

  // Bloquear scroll del body mientras el modal está abierto.
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  const setField = useCallback((field, value) => {
    setData((prev) => ({ ...prev, [field]: value }));
    setDirty(true);
  }, []);

  const setNested = useCallback((group, key, value) => {
    setData((prev) => ({
      ...prev,
      [group]: { ...(prev[group] || {}), [key]: value },
    }));
    setDirty(true);
  }, []);

  const setLocation = useCallback((index, value) => {
    setData((prev) => {
      const loc = Array.isArray(prev.location) ? [...prev.location] : ['', ''];
      loc[index] = value;
      return { ...prev, location: loc };
    });
    setDirty(true);
  }, []);

  const setOperation = useCallback((type) => {
    setData((prev) => ({
      ...prev,
      isForSale: type === 'sale',
      isForRent: type === 'rent',
    }));
    setDirty(true);
  }, []);

  const { score, breakdown, missingRequired } = useMemo(
    () => computePropertyScore(data || {}),
    [data]
  );
  const level = getScoreLevel(score);

  const land = data ? isLandType(data.propertyType) : false;
  const photos = data && Array.isArray(data.photo) ? data.photo : [];
  const docs = data && Array.isArray(data.documentation) ? data.documentation : [];

  const buildFormData = (d) => {
    const fd = new FormData();
    const put = (k, v) => fd.append(k, v === null || v === undefined ? '' : v);

    put('propertyType', d.propertyType);
    put('statusProperty', d.statusProperty ?? false);
    put('videoLink', d.videoLink);
    put('currency', d.currency);
    put('price', d.price);
    put('currencyExpenses', d.currencyExpenses);
    put('expenses', d.expenses);
    put('totalSquareMeters', d.totalSquareMeters);
    put('coveredSquareMeters', d.coveredSquareMeters);
    put('semiCoveredSquareMeters', d.semiCoveredSquareMeters);
    put(
      'uncovered',
      isLandType(d.propertyType) ? d.land || '0' : d.uncovered
    );
    put('land', d.land);
    put('age', d.age);
    put('commissionSellerType', d.commissionSellerType);
    put('commissionBuyerType', d.commissionBuyerType);
    put('sellerCommission', d.sellerCommission);
    put('buyerCommission', d.buyerCommission);
    put('availableDate', d.availableDate);
    put('expirationDate', d.expirationDate);
    put('street', d.street);
    put('number', d.number);
    put('country', d.country);
    put('province', d.province);
    put('departments', d.departments);
    put('locality', d.locality);
    put('neighborhood', d.neighborhood);
    put('privateNeighborhood', d.privateNeighborhood);
    put('title', d.title);
    put('description', d.description);
    put('floorPlans', d.floorPlans);
    put('isForSale', !!isTruthy(d.isForSale));
    put('isForRent', !!isTruthy(d.isForRent));
    put('isFinished', !!isTruthy(d.isFinished));
    put('isUnderDevelopment', !!isTruthy(d.isUnderDevelopment));
    put('environments', d.environments);
    put('rooms', d.rooms);
    put('bathrooms', d.bathrooms);
    put('toilettes', d.toilettes);
    put('garages', d.garages);
    put('propertyState', d.propertyState);
    put('cantidad', d.cantidad);

    const loc = Array.isArray(d.location) ? d.location : ['', ''];
    fd.append('location[]', loc[0] ?? '');
    fd.append('location[]', loc[1] ?? '');

    fd.append('amenities', JSON.stringify(d.amenities || {}));
    fd.append('environmentsOptions', JSON.stringify(d.environmentsOptions || {}));
    fd.append('services', JSON.stringify(d.services || {}));
    fd.append('characteristics', JSON.stringify(d.characteristics || {}));
    fd.append('detailsProperty', JSON.stringify(d.detailsProperty || {}));

    if (d.sellerId !== null && d.sellerId !== undefined && d.sellerId !== '') {
      fd.append('sellerId', d.sellerId);
    }
    return fd;
  };

  const handleSave = async () => {
    if (!data) return;
    setSaving(true);
    try {
      await dispatch(editProperty(propertyId, buildFormData(data)));
      toast.success('Cambios guardados correctamente.', { autoClose: 3500 });
      setSavedOnce(true);
      await fetchProperty();
      setEditing(false);
    } catch (error) {
      const serverError = error?.response?.data;
      toast.error(
        serverError?.message || 'No se pudieron guardar los cambios.',
        { autoClose: 5000 }
      );
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    if (dirty) {
      const ok = window.confirm(
        'Tenés cambios sin guardar. ¿Cerrar de todos modos?'
      );
      if (!ok) return;
    }
    if (savedOnce && typeof onSaved === 'function') onSaved();
    onClose();
  };

  const handleAfterApproval = () => {
    if (typeof onSaved === 'function') onSaved();
    onClose();
  };

  // ===== Render helpers =====
  const DataItem = ({ label, required, children }) => (
    <div className={styles.dataItem}>
      <span className={styles.dataLabel}>
        {label}
        {required && <span className={styles.reqStar}>*</span>}
      </span>
      {children}
    </div>
  );

  const embedUrl = data ? youtubeEmbedUrl(data.videoLink) : null;

  return (
    <div className={styles.overlay} onClick={handleClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className={styles.header}>
          <h3 className={styles.headerTitle}>
            👁 Vista previa de publicación
            <span className={styles.headerId}>#{propertyId}</span>
          </h3>
          <div className={styles.headerActions}>
            <button
              className={`${styles.editToggle} ${
                editing ? styles.editToggleActive : ''
              }`}
              onClick={() => setEditing((v) => !v)}
              disabled={loading}
            >
              {editing ? '👁 Ver como publicación' : '✎ Editar'}
            </button>
            {editing && (
              <button
                className={styles.saveBtn}
                onClick={handleSave}
                disabled={saving || !dirty}
              >
                {saving ? 'Guardando…' : 'Guardar cambios'}
              </button>
            )}
            <button className={styles.closeBtn} onClick={handleClose}>
              ×
            </button>
          </div>
        </div>

        {loading || !data ? (
          <div className={styles.loading}>Cargando propiedad…</div>
        ) : (
          <>
            <div className={styles.body}>
              {/* ===== Contenido principal ===== */}
              <div className={styles.content}>
                {/* Galería */}
                <div className={`${styles.section} ${styles.gallery}`}>
                  {photos.length > 0 ? (
                    <>
                      <img
                        src={photos[activePhoto]}
                        alt={`Foto ${activePhoto + 1}`}
                        className={styles.mainPhoto}
                        style={{
                          width: '100%',
                          height: '460px',
                          objectFit: 'cover',
                          background: '#eceaf5',
                        }}
                      />
                      {photos.length > 1 && (
                        <div className={styles.thumbStrip}>
                          {photos.map((p, i) => (
                            <img
                              key={i}
                              src={p}
                              alt={`Miniatura ${i + 1}`}
                              className={`${styles.thumb} ${
                                i === activePhoto ? styles.thumbActive : ''
                              }`}
                              onClick={() => setActivePhoto(i)}
                            />
                          ))}
                        </div>
                      )}
                      <div className={styles.photoCount}>
                        {photos.length} foto(s) · Foto {activePhoto + 1}
                      </div>
                    </>
                  ) : (
                    <div className={styles.noPhotos}>
                      ⚠ Sin fotos cargadas
                    </div>
                  )}
                </div>

                {/* Operación + estado + título + precio */}
                <div className={styles.section}>
                  <div className={styles.badgeRow}>
                    {editing ? (
                      <>
                        <button
                          type="button"
                          className={`${styles.badge} ${
                            isTruthy(data.isForSale)
                              ? styles.badgeSale
                              : styles.badgeState
                          }`}
                          onClick={() => setOperation('sale')}
                        >
                          Venta
                        </button>
                        <button
                          type="button"
                          className={`${styles.badge} ${
                            isTruthy(data.isForRent)
                              ? styles.badgeRent
                              : styles.badgeState
                          }`}
                          onClick={() => setOperation('rent')}
                        >
                          Alquiler
                        </button>
                      </>
                    ) : (
                      <>
                        {isTruthy(data.isForSale) && (
                          <span className={`${styles.badge} ${styles.badgeSale}`}>
                            Venta
                          </span>
                        )}
                        {isTruthy(data.isForRent) && (
                          <span className={`${styles.badge} ${styles.badgeRent}`}>
                            Alquiler
                          </span>
                        )}
                        {isTruthy(data.isUnderDevelopment) && (
                          <span className={`${styles.badge} ${styles.badgeDev}`}>
                            En desarrollo
                          </span>
                        )}
                      </>
                    )}
                    {!isTruthy(data.isForSale) && !isTruthy(data.isForRent) && (
                      <span className={styles.missingTag}>
                        Falta tipo de operación
                      </span>
                    )}
                    <span className={`${styles.badge} ${styles.badgeState}`}>
                      {editing ? (
                        <EditableSelect
                          editing
                          value={data.propertyState}
                          onChange={(v) => setField('propertyState', v)}
                          options={propertyStateOptions}
                          required
                        />
                      ) : (
                        getLabelFromOptions(
                          propertyStateOptions,
                          data.propertyState
                        )
                      )}
                    </span>
                  </div>

                  <div className={styles.titleBlock}>
                    {editing ? (
                      <EditableText
                        editing
                        value={data.title}
                        onChange={(v) => setField('title', v)}
                        placeholder="Título de la publicación"
                        required
                      />
                    ) : (
                      <h2 className={styles.propTitle}>
                        {data.title || (
                          <span className={styles.missingTag}>
                            Falta título (obligatorio)
                          </span>
                        )}
                      </h2>
                    )}

                    <div className={styles.priceRow}>
                      {editing ? (
                        <>
                          <EditableSelect
                            editing
                            value={data.currency}
                            onChange={(v) => setField('currency', v)}
                            options={currencyOptions}
                          />
                          <EditableNumber
                            editing
                            value={data.price}
                            onChange={(v) => setField('price', v)}
                            placeholder="Precio"
                            required
                          />
                        </>
                      ) : (
                        <span className={styles.price}>
                          {data.price ? (
                            `${data.currency || ''} $${formatPrice(data.price)}`
                          ) : (
                            <span className={styles.missingTag}>
                              Falta precio (obligatorio)
                            </span>
                          )}
                        </span>
                      )}
                      {(data.expenses || editing) && (
                        <span className={styles.expenses}>
                          + expensas{' '}
                          {editing ? (
                            <EditableNumber
                              editing
                              value={data.expenses}
                              onChange={(v) => setField('expenses', v)}
                              placeholder="0"
                            />
                          ) : (
                            `$${formatPrice(data.expenses)}`
                          )}
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Datos principales */}
                <div className={styles.section}>
                  <h4 className={styles.sectionTitle}>Datos principales</h4>
                  <div className={styles.dataGrid}>
                    <DataItem label="Tipo de propiedad" required>
                      <EditableSelect
                        editing={editing}
                        value={data.propertyType}
                        onChange={(v) => setField('propertyType', v)}
                        options={propertyTypeOptions}
                        required
                        displayValue={getLabelFromOptions(
                          propertyTypeOptions,
                          data.propertyType
                        )}
                      />
                    </DataItem>
                    <DataItem label="Superficie total (m²)">
                      <EditableNumber
                        editing={editing}
                        value={data.totalSquareMeters}
                        onChange={(v) => setField('totalSquareMeters', v)}
                        suffix=" m²"
                      />
                    </DataItem>
                    <DataItem label="Terreno (m²)" required>
                      <EditableNumber
                        editing={editing}
                        value={data.land}
                        onChange={(v) => setField('land', v)}
                        required
                        suffix=" m²"
                      />
                    </DataItem>
                    {!land && (
                      <>
                        <DataItem label="Cubierta (m²)" required>
                          <EditableNumber
                            editing={editing}
                            value={data.coveredSquareMeters}
                            onChange={(v) => setField('coveredSquareMeters', v)}
                            required
                            suffix=" m²"
                          />
                        </DataItem>
                        <DataItem label="Descubierta (m²)" required>
                          <EditableNumber
                            editing={editing}
                            value={data.uncovered}
                            onChange={(v) => setField('uncovered', v)}
                            required
                            suffix=" m²"
                          />
                        </DataItem>
                        <DataItem label="Semicubierta (m²)">
                          <EditableNumber
                            editing={editing}
                            value={data.semiCoveredSquareMeters}
                            onChange={(v) =>
                              setField('semiCoveredSquareMeters', v)
                            }
                            suffix=" m²"
                          />
                        </DataItem>
                        <DataItem label="Ambientes" required>
                          <EditableNumber
                            editing={editing}
                            value={data.environments}
                            onChange={(v) => setField('environments', v)}
                            required
                          />
                        </DataItem>
                        <DataItem label="Dormitorios" required>
                          <EditableNumber
                            editing={editing}
                            value={data.rooms}
                            onChange={(v) => setField('rooms', v)}
                            required
                          />
                        </DataItem>
                        <DataItem label="Baños" required>
                          <EditableNumber
                            editing={editing}
                            value={data.bathrooms}
                            onChange={(v) => setField('bathrooms', v)}
                            required
                          />
                        </DataItem>
                        <DataItem label="Toilettes" required>
                          <EditableNumber
                            editing={editing}
                            value={data.toilettes}
                            onChange={(v) => setField('toilettes', v)}
                            required
                          />
                        </DataItem>
                        <DataItem label="Cocheras" required>
                          <EditableNumber
                            editing={editing}
                            value={data.garages}
                            onChange={(v) => setField('garages', v)}
                            required
                          />
                        </DataItem>
                        <DataItem label="Pisos" required>
                          <EditableNumber
                            editing={editing}
                            value={data.floorPlans}
                            onChange={(v) => setField('floorPlans', v)}
                            required
                          />
                        </DataItem>
                      </>
                    )}
                    <DataItem label="Antigüedad (años)">
                      <EditableNumber
                        editing={editing}
                        value={data.age}
                        onChange={(v) => setField('age', v)}
                      />
                    </DataItem>
                    {typesWithCantidad.includes(data.propertyType) && (
                      <DataItem label="Cantidad">
                        <EditableNumber
                          editing={editing}
                          value={data.cantidad}
                          onChange={(v) => setField('cantidad', v)}
                        />
                      </DataItem>
                    )}
                  </div>
                </div>

                {/* Descripción */}
                <div className={styles.section}>
                  <h4 className={styles.sectionTitle}>Descripción</h4>
                  <EditableTextarea
                    editing={editing}
                    value={data.description}
                    onChange={(v) => setField('description', v)}
                    placeholder="Descripción de la propiedad"
                    required
                    rows={6}
                  />
                </div>

                {/* Comisiones */}
                <div className={styles.section}>
                  <h4 className={styles.sectionTitle}>Comisiones</h4>
                  <div className={styles.dataGrid}>
                    <DataItem label="Tipo comisión vendedor">
                      <EditableSelect
                        editing={editing}
                        value={data.commissionSellerType}
                        onChange={(v) => setField('commissionSellerType', v)}
                        options={commissionTypeOptions}
                        displayValue={getLabelFromOptions(
                          commissionTypeOptions,
                          data.commissionSellerType
                        )}
                      />
                    </DataItem>
                    <DataItem label="Comisión vendedor" required>
                      <EditableNumber
                        editing={editing}
                        value={data.sellerCommission}
                        onChange={(v) => setField('sellerCommission', v)}
                        required
                      />
                    </DataItem>
                    <DataItem label="Tipo comisión comprador">
                      <EditableSelect
                        editing={editing}
                        value={data.commissionBuyerType}
                        onChange={(v) => setField('commissionBuyerType', v)}
                        options={commissionTypeOptions}
                        displayValue={getLabelFromOptions(
                          commissionTypeOptions,
                          data.commissionBuyerType
                        )}
                      />
                    </DataItem>
                    <DataItem label="Comisión comprador" required>
                      <EditableNumber
                        editing={editing}
                        value={data.buyerCommission}
                        onChange={(v) => setField('buyerCommission', v)}
                        required
                      />
                    </DataItem>
                  </div>
                </div>

                {/* Ubicación */}
                <div className={styles.section}>
                  <h4 className={styles.sectionTitle}>Ubicación</h4>
                  <div className={styles.dataGrid}>
                    <DataItem label="Calle" required>
                      <EditableText
                        editing={editing}
                        value={data.street}
                        onChange={(v) => setField('street', v)}
                        required
                      />
                    </DataItem>
                    <DataItem label="Número" required>
                      <EditableText
                        editing={editing}
                        value={data.number}
                        onChange={(v) => setField('number', v)}
                        required
                      />
                    </DataItem>
                    <DataItem label="Localidad" required>
                      <EditableText
                        editing={editing}
                        value={data.locality}
                        onChange={(v) => setField('locality', v)}
                        required
                      />
                    </DataItem>
                    <DataItem label="Departamento" required>
                      <EditableText
                        editing={editing}
                        value={data.departments}
                        onChange={(v) => setField('departments', v)}
                        required
                      />
                    </DataItem>
                    <DataItem label="Provincia" required>
                      <EditableText
                        editing={editing}
                        value={data.province}
                        onChange={(v) => setField('province', v)}
                        required
                      />
                    </DataItem>
                    <DataItem label="País" required>
                      <EditableText
                        editing={editing}
                        value={data.country}
                        onChange={(v) => setField('country', v)}
                        required
                      />
                    </DataItem>
                    <DataItem label="Barrio">
                      <EditableText
                        editing={editing}
                        value={data.neighborhood}
                        onChange={(v) => setField('neighborhood', v)}
                      />
                    </DataItem>
                    <DataItem label="Barrio privado">
                      <EditableText
                        editing={editing}
                        value={data.privateNeighborhood}
                        onChange={(v) => setField('privateNeighborhood', v)}
                      />
                    </DataItem>
                  </div>

                  <div className={styles.locationRow} style={{ marginTop: 14 }}>
                    {editing ? (
                      <>
                        <DataItem label="Latitud">
                          <EditableText
                            editing
                            value={
                              Array.isArray(data.location)
                                ? data.location[0]
                                : ''
                            }
                            onChange={(v) => setLocation(0, v)}
                          />
                        </DataItem>
                        <DataItem label="Longitud">
                          <EditableText
                            editing
                            value={
                              Array.isArray(data.location)
                                ? data.location[1]
                                : ''
                            }
                            onChange={(v) => setLocation(1, v)}
                          />
                        </DataItem>
                      </>
                    ) : (
                      Array.isArray(data.location) &&
                      data.location[0] &&
                      data.location[1] && (
                        <a
                          className={styles.mapLink}
                          href={`https://www.google.com/maps/search/?api=1&query=${data.location[0]},${data.location[1]}`}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          📍 Ver en Google Maps ({data.location[0]},{' '}
                          {data.location[1]})
                        </a>
                      )
                    )}
                  </div>
                </div>

                {/* Características */}
                <div className={styles.section}>
                  <h4 className={styles.sectionTitle}>Características</h4>
                  <EditableCheckboxGroup
                    editing={editing}
                    values={data.characteristics || {}}
                    labelsMap={characteristicLabels}
                    onChange={(k, v) => setNested('characteristics', k, v)}
                  />
                </div>

                {/* Amenities */}
                <div className={styles.section}>
                  <h4 className={styles.sectionTitle}>Amenities</h4>
                  <EditableCheckboxGroup
                    editing={editing}
                    values={data.amenities || {}}
                    labelsMap={amenityLabels}
                    onChange={(k, v) => setNested('amenities', k, v)}
                  />
                </div>

                {/* Ambientes */}
                <div className={styles.section}>
                  <h4 className={styles.sectionTitle}>Ambientes</h4>
                  <EditableCheckboxGroup
                    editing={editing}
                    values={data.environmentsOptions || {}}
                    labelsMap={environmentLabels}
                    onChange={(k, v) => setNested('environmentsOptions', k, v)}
                  />
                </div>

                {/* Servicios */}
                <div className={styles.section}>
                  <h4 className={styles.sectionTitle}>Servicios</h4>
                  <EditableCheckboxGroup
                    editing={editing}
                    values={data.services || {}}
                    labelsMap={serviceLabels}
                    onChange={(k, v) => setNested('services', k, v)}
                  />
                </div>

                {/* Detalles adicionales */}
                <div className={styles.section}>
                  <h4 className={styles.sectionTitle}>Detalles adicionales</h4>
                  <EditableCheckboxGroup
                    editing={editing}
                    values={data.detailsProperty || {}}
                    labelsMap={detailLabels}
                    onChange={(k, v) => setNested('detailsProperty', k, v)}
                  />
                </div>

                {/* Video */}
                <div className={styles.section}>
                  <h4 className={styles.sectionTitle}>Video</h4>
                  {editing ? (
                    <EditableText
                      editing
                      value={data.videoLink}
                      onChange={(v) => setField('videoLink', v)}
                      placeholder="https://youtube.com/..."
                    />
                  ) : embedUrl ? (
                    <iframe
                      className={styles.videoEmbed}
                      src={embedUrl}
                      title="Video de la propiedad"
                      allowFullScreen
                    />
                  ) : isValidYoutubeUrl(data.videoLink) ? (
                    <a
                      className={styles.videoLink}
                      href={data.videoLink}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      ▶ Ver video
                    </a>
                  ) : (
                    <span className={styles.emptyTag}>Sin video</span>
                  )}
                </div>

                {/* Documentación */}
                <div className={styles.section}>
                  <h4 className={styles.sectionTitle}>
                    Documentación ({docs.length})
                  </h4>
                  {docs.length === 0 ? (
                    <span className={styles.missingTag}>
                      Sin documentación adjunta
                    </span>
                  ) : (
                    <div className={styles.docList}>
                      {docs.map((doc, i) => (
                        <div key={i} className={styles.docRow}>
                          <span className={styles.docName}>
                            📄 {getFileNameFromUrl(doc)}
                          </span>
                          <button
                            type="button"
                            className={`${styles.docBtn} ${styles.docBtnPrimary}`}
                            onClick={() =>
                              setDocPreview(docPreview === doc ? null : doc)
                            }
                          >
                            {docPreview === doc ? 'Ocultar' : 'Ver'}
                          </button>
                          <a
                            className={styles.docBtn}
                            href={doc}
                            target="_blank"
                            rel="noopener noreferrer"
                            download
                          >
                            Descargar
                          </a>
                        </div>
                      ))}
                    </div>
                  )}
                  {docPreview &&
                    (isPdfUrl(docPreview) ? (
                      <iframe
                        className={styles.docViewer}
                        src={docPreview}
                        title="Documento"
                      />
                    ) : isImageUrl(docPreview) ? (
                      <img
                        className={styles.docViewerImg}
                        src={docPreview}
                        alt="Documento"
                      />
                    ) : (
                      <iframe
                        className={styles.docViewer}
                        src={`https://docs.google.com/gview?embedded=true&url=${encodeURIComponent(
                          docPreview
                        )}`}
                        title="Documento"
                      />
                    ))}
                </div>
              </div>

              {/* ===== Sidebar: puntuación ===== */}
              <div className={styles.sidebar}>
                <div className={styles.scoreCard}>
                  <div
                    className={styles.scoreCircle}
                    style={{
                      background: `conic-gradient(${level.color} ${
                        score * 3.6
                      }deg, #ece9f5 0deg)`,
                    }}
                  >
                    <div
                      style={{
                        width: 96,
                        height: 96,
                        borderRadius: '50%',
                        background: '#fff',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <span
                        className={styles.scoreNumber}
                        style={{ color: level.color }}
                      >
                        {score}
                      </span>
                      <span
                        className={styles.scoreOutOf}
                        style={{ color: '#8b83a8' }}
                      >
                        / 100
                      </span>
                    </div>
                  </div>
                  <div
                    className={styles.scoreLevel}
                    style={{ color: level.color }}
                  >
                    {level.label}
                  </div>
                </div>

                <div className={styles.sidebarTitle}>Desglose</div>
                {breakdown.map((b) => (
                  <div key={b.key} className={styles.breakdownItem}>
                    <div className={styles.breakdownHead}>
                      <span>{b.label}</span>
                      <span className={styles.breakdownPts}>
                        {b.points}/{b.max}
                      </span>
                    </div>
                    <div className={styles.progressTrack}>
                      <div
                        className={styles.progressFill}
                        style={{
                          width: `${(b.points / b.max) * 100}%`,
                          background: b.ok ? '#28a745' : '#f0ad4e',
                        }}
                      />
                    </div>
                    <div className={styles.breakdownHint}>{b.hint}</div>
                  </div>
                ))}

                <div className={styles.sidebarTitle}>
                  Obligatorios faltantes
                </div>
                {missingRequired.length === 0 ? (
                  <div className={styles.allGood}>
                    ✓ Todos los datos obligatorios están completos
                  </div>
                ) : (
                  <ul className={styles.missingList}>
                    {missingRequired.map((m) => (
                      <li key={m.field} className={styles.missingItem}>
                        ⚠ {m.label}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>

            {/* ===== Footer: aprobación ===== */}
            <div className={styles.footer}>
              {dirty && (
                <div className={styles.dirtyBanner}>
                  ⚠ Tenés cambios sin guardar. Guardalos antes de aprobar para
                  que se reflejen en la publicación.
                </div>
              )}
              <ApprovalActions
                propertyId={propertyId}
                onActionComplete={handleAfterApproval}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default PropertyPreviewModal;
